import type { GUID } from "@faro-lotv/foundation";
import {
  isCocoRleMask,
  type AiCocoRleMask,
  type AiFindingMask,
  type AiMaskFile,
  type BoundingBoxCoordinates,
  type ProjectApi,
} from "@faro-lotv/service-wires";
import { useEffect, useState } from "react";

/**
 * The loaded masks by element ID. The boxes' `hasMask` flags are loaded once per project, so the masks they refer to
 * are kept for the session as well. Across sessions, the browser revalidates them with the response's ETag.
 */
const maskFiles = new Map<GUID, Promise<AiMaskFile | undefined>>();

/**
 * @param projectApi The client of the project of the element.
 * @param elementId The element whose masks to load.
 * @returns The masks of the element's findings, or undefined if it has none, fetched once per element. A failed
 * request is not cached, so it is tried again next time.
 */
export function fetchAiMaskFile(
  projectApi: ProjectApi,
  elementId: GUID,
): Promise<AiMaskFile | undefined> {
  let file = maskFiles.get(elementId);
  if (!file) {
    file = projectApi.aiMasks(elementId);
    file.catch(() => maskFiles.delete(elementId));
    maskFiles.set(elementId, file);
  }
  return file;
}

/** The masks of an element's findings */
export type AiMasksState = {
  /** The masks by bounding box ID, of the encodings this viewer can draw */
  masks: Map<string, AiCocoRleMask>;

  /** The size of the image the masks were computed on, in pixels */
  imageSize?: AiMaskFile["imageSize"];

  /** Whether the masks are still loading. The findings wait for them, so they don't flash as boxes first. */
  isLoading: boolean;
};

type LoadedMasks = Omit<AiMasksState, "isLoading"> & { elementId: GUID };

const NO_MASKS = new Map<string, AiCocoRleMask>();

/**
 * @param projectApi The client of the project of the element.
 * @param elementId The element whose masks to load, or undefined if no mask is drawn.
 * @returns The masks of the element's findings. Without masks, or if they can't be loaded, there are none, and the
 * findings are drawn without them.
 */
export function useAiMasks(
  projectApi: ProjectApi,
  elementId: GUID | undefined,
): AiMasksState {
  const [loaded, setLoaded] = useState<LoadedMasks>();

  useEffect(() => {
    if (!elementId) return;

    let cancelled = false;
    fetchAiMaskFile(projectApi, elementId)
      .then((file) => {
        if (cancelled) return;
        setLoaded({
          elementId,
          masks: new Map(
            file?.masks.filter(isCocoRleMask).map((mask) => [mask.boxId, mask]),
          ),
          imageSize: file?.imageSize,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.warn("Drawing the AI findings without their masks:", error);
        setLoaded({ elementId, masks: NO_MASKS });
      });
    return () => {
      cancelled = true;
    };
  }, [projectApi, elementId]);

  if (!elementId) return { masks: NO_MASKS, isLoading: false };
  if (loaded?.elementId !== elementId) {
    return { masks: NO_MASKS, isLoading: true };
  }
  return { masks: loaded.masks, imageSize: loaded.imageSize, isLoading: false };
}

/**
 * @param mask The mask of a finding.
 * @param imageSize The size of the image the mask was computed on.
 * @returns The part of the image the mask covers, in the normalized coordinates of bounding boxes.
 */
export function maskBoxCoordinates(
  mask: AiFindingMask,
  imageSize: AiMaskFile["imageSize"],
): BoundingBoxCoordinates {
  const { x, y, width, height } = mask.boundingBox;
  return {
    x1: x / imageSize.width,
    y1: y / imageSize.height,
    x2: (x + width) / imageSize.width,
    y2: (y + height) / imageSize.height,
  };
}
